'use client';

import {
  memo,
  useRef,
  useState,
  type CSSProperties,
  type MouseEvent,
  type ReactNode,
  type Ref,
} from 'react';
import Image from 'next/image';
import { Search } from 'lucide-react';
import SiteAccountControls from './site-account-controls';
import HeroBannerLive2d, {
  type BannerLive2dSlot,
} from './hero-banner-live2d';
import type { SiteNavItem } from './use-site-navigation';
import type { BannerHeroSource } from '../lib/heroes/banner';
import { coverTheme } from '../lib/heroes/theme';
import { staticUrl } from '../lib/config/static-assets';
import './site-chrome.css';

export type SiteSearchSuggestion = {
  id: string;
  title: string;
  meta: string;
};
export type SearchProps = {
  query: string;
  onQuery: (query: string) => void;
  onSearch: () => void;
  placeholder: string;
  suggestions?: SiteSearchSuggestion[];
  onSuggestion?: (suggestion: SiteSearchSuggestion) => void;
};
export function SiteSearch({
  query,
  onQuery,
  onSearch,
  placeholder,
  suggestions = [],
  onSuggestion,
  compact = false,
}: SearchProps & { compact?: boolean }) {
  return (
    <form
      className={`gk-search${compact ? ' gk-search-small' : ''}`}
      aria-label={compact ? '顶部搜索' : '站内搜索'}
      onSubmit={(event) => {
        event.preventDefault();
        onSearch();
      }}
    >
      <button type="submit" aria-label="搜索">
        <Search size={compact ? 18 : 22} />
      </button>
      <input
        aria-label={placeholder}
        placeholder={placeholder}
        type="search"
        value={query}
        onChange={(event) => onQuery(event.target.value)}
      />
      {query.trim() && suggestions.length > 0 && (
        <div className="gk-search-suggestions" role="listbox" aria-label="搜索联想">
          {suggestions.map((suggestion) => (
            <button
              type="button"
              role="option"
              key={suggestion.id}
              onMouseDown={(event) => event.preventDefault()}
              onClick={() => onSuggestion?.(suggestion)}
            >
              <Search aria-hidden="true" size={16} />
              <span>{suggestion.title}</span>
              <small>{suggestion.meta}</small>
            </button>
          ))}
          <button
            className="gk-search-all"
            type="submit"
            role="option"
            onMouseDown={(event) => event.preventDefault()}
          >
            <Search aria-hidden="true" size={16} />
            <span>查看“{query.trim()}”的全部结果</span>
            <small>SEARCH</small>
          </button>
        </div>
      )}
    </form>
  );
}
export function SiteHeader({
  banner,
  compact = false,
  search,
  searchPanel,
  gameDomain,
}: {
  banner: boolean;
  compact?: boolean;
  search?: SearchProps;
  searchPanel?: ReactNode;
  gameDomain?: string;
}) {
  const [searchActive, setSearchActive] = useState(false);
  const cancelSearch = () => {
    search?.onQuery('');
    setSearchActive(false);
    if (document.activeElement instanceof HTMLElement) {
      document.activeElement.blur();
    }
  };
  return (
    <>
      <header
        className={`gk-header${banner ? ' gk-header-overlay' : ''}${compact ? ' gk-header-compact' : ''}${searchActive ? ' gk-search-active' : ''}`}
      >
        <a href="/" className="gk-brand" aria-label="GAME WIKI 首页">
          <b>GW</b>
          <span>GAME WIKI</span>
        </a>
        {search && (
          <div
            className="gk-header-search"
            onFocusCapture={() => setSearchActive(true)}
          >
            <SiteSearch {...search} compact />
            {searchPanel}
          </div>
        )}
        <SiteAccountControls gameDomain={gameDomain} />
        {search && (
          <button
            className="gk-search-cancel"
            type="button"
            onClick={cancelSearch}
          >
            取消
          </button>
        )}
      </header>
      {!banner && <div className="gk-header-space" />}
    </>
  );
}
function SiteHeroComponent({
  title,
  eyebrow,
  background = staticUrl('/assets/game-wiki/wiki-hero.portal.jpg'),
  live2dSlots = [],
  bannerHeroes = [],
  gameId = 'arcanatactics',
  initialTheme,
  children,
  compact = false,
  ...search
}: SearchProps & {
  title: string;
  eyebrow: string;
  background?: string;
  live2dSlots?: BannerLive2dSlot[];
  bannerHeroes?: BannerHeroSource[];
  gameId?: string;
  initialTheme?: string;
  children?: ReactNode;
  compact?: boolean;
}) {
  const sectionRef = useRef<HTMLElement>(null);
  const [expanded, setExpanded] = useState(false);
  const [live2dTheme, setLive2dTheme] = useState<string | null>(() => {
    const id = live2dSlots[0]?.id;
    if (!id) return initialTheme || null;
    const hero =
      bannerHeroes.find((item) => item.id === id && item.gameId === gameId) ||
      bannerHeroes.find((item) => item.id === id);
    return hero ? coverTheme(hero) : null;
  });
  const hasLive2d = live2dSlots.length > 0;
  const initialArcanaTheme = gameId === 'arcanatactics' && !!initialTheme && !hasLive2d;
  const themed = (hasLive2d || initialArcanaTheme) && !!live2dTheme;

  const toggleExpand = () => {
    const next = !expanded;
    setExpanded(next);
    if (next) {
      requestAnimationFrame(() => {
        sectionRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
      });
    }
  };

  const handleHeroClick = (event: MouseEvent<HTMLElement>) => {
    if (!hasLive2d || !(event.target instanceof Element)) return;
    if (
      event.target.closest(
        'a, button, form, input, textarea, select, label, summary, [role="button"], [contenteditable="true"]',
      ) ||
      window.getSelection()?.toString()
    ) return;
    toggleExpand();
  };

  return (
    <section
      ref={sectionRef}
      onClick={handleHeroClick}
      className={[
        'gk-hero',
        compact ? 'gk-hero-compact' : '',
        hasLive2d || initialArcanaTheme ? 'has-live2d' : '',
        expanded ? 'is-expanded' : '',
        themed ? `hero-theme-${live2dTheme}` : '',
      ]
        .filter(Boolean)
        .join(' ')}
      aria-label="Wiki 搜索"
    >
      <div className="gk-hero-bg" aria-hidden="true">
        {!themed && (
          <Image
            className="gk-hero-image"
            src={staticUrl(background)}
            alt=""
            fill
            unoptimized
            priority
            sizes="100vw"
          />
        )}
      </div>
      {hasLive2d && (
        <HeroBannerLive2d
          slots={live2dSlots}
          heroes={bannerHeroes}
          gameId={gameId}
          playing={expanded}
          onThemeChange={setLive2dTheme}
        />
      )}
      <div className="gk-hero-copy">
        {eyebrow && <p className="gk-eyebrow">{eyebrow}</p>}
        <h1>{title}</h1>
        <SiteSearch {...search} />
        <div className="gk-hero-below">{children}</div>
      </div>
      {hasLive2d && (
        <button
          type="button"
          className={`gk-hero-expand-btn${expanded ? ' is-expanded' : ''}`}
          aria-label={expanded ? '收起全屏' : '全屏展示立绘'}
          title={expanded ? '收起' : '全屏'}
          onClick={toggleExpand}
        >
          <svg viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
            <rect x="5" y="3.5" width="14" height="2.2" rx="1.1" />
            <path d="M5.5 9.5h13L12 19.5 5.5 9.5z" />
          </svg>
        </button>
      )}
    </section>
  );
}
export const SiteHero = memo(SiteHeroComponent);
export function SiteNavigation<T extends string>({
  items,
  ready = true,
  active,
  compact = false,
  marker,
  onNavigate,
  sectionFilter,
  gameDomain,
  ...search
}: SearchProps & {
  items: readonly SiteNavItem<T>[];
  ready?: boolean;
  active: T;
  compact?: boolean;
  marker: Ref<HTMLDivElement>;
  onNavigate: (event: MouseEvent<HTMLAnchorElement>, id: T) => void;
  sectionFilter?: {
    itemId: T;
    value: string;
    options: readonly string[];
    onSelect: (value: string) => void;
  };
  gameDomain?: string;
}) {
  const [mobileSearchOpen, setMobileSearchOpen] = useState(false);
  const navigationRef = useRef<HTMLElement>(null);
  const closeMobileSearch = () => {
    setMobileSearchOpen(false);
    search.onQuery('');
  };
  return (
    <>
      <div ref={marker} className="gk-nav-marker" aria-hidden="true" />
      <nav
        ref={navigationRef}
        className={`gk-navigation${compact ? ' gk-navigation-compact' : ''}${mobileSearchOpen ? ' gk-navigation-search-open' : ''}`}
        aria-label="栏目导航"
        style={ready ? undefined : { visibility: 'hidden' }}
      >
        <div className="gk-nav-inner">
          <div
            className="gk-nav-links"
            style={{ '--nav-count': items.length, '--nav-regular-count': Math.max(items.length - 1, 1) } as CSSProperties}
          >
            {items.map((item) => {
              const filter = sectionFilter?.itemId === item.id ? sectionFilter : null;
              const link = (
                <a
                  href={item.href}
                  aria-current={active === item.id ? 'page' : undefined}
                  onClick={(event) => onNavigate(event, item.id)}
                >
                  {item.title}
                </a>
              );
              if (!filter) return <div className="gk-nav-item" key={item.id}>{link}</div>;
              return (
                <div
                  className={`gk-nav-item gk-nav-item-filter${active === item.id ? ' is-active' : ''}`}
                  key={item.id}
                >
                  {link}
                  <details
                    onMouseEnter={(event) => {
                      if (window.matchMedia('(hover: hover) and (pointer: fine)').matches) event.currentTarget.open = true;
                    }}
                    onMouseLeave={(event) => {
                      if (window.matchMedia('(hover: hover) and (pointer: fine)').matches) event.currentTarget.open = false;
                    }}
                  >
                    <summary
                      aria-label={`当前分类：${filter.value}，点击筛选`}
                      onClick={(event) => {
                        event.preventDefault();
                        const details = event.currentTarget.closest('details');
                        if (details) details.open = !details.open;
                      }}
                    >
                      <span className="gk-nav-filter-value">{filter.value}</span><span aria-hidden="true">⌄</span>
                    </summary>
                    <div className="gk-nav-filter-menu" role="menu">
                      {filter.options.map((option) => (
                        <button
                          type="button"
                          role="menuitemradio"
                          aria-checked={filter.value === option}
                          key={option}
                          onClick={(event) => {
                            filter.onSelect(option);
                            event.currentTarget.closest('details')?.removeAttribute('open');
                          }}
                        >
                          <span>{option}</span>
                          {filter.value === option && <span aria-hidden="true">✓</span>}
                        </button>
                      ))}
                    </div>
                  </details>
                </div>
              );
            })}
          </div>
          <button
            className="gk-nav-mobile-search-toggle"
            type="button"
            aria-label="打开搜索"
            aria-expanded={mobileSearchOpen}
            onClick={() => {
              setMobileSearchOpen(true);
              requestAnimationFrame(() => navigationRef.current?.querySelector<HTMLInputElement>('input[type="search"]')?.focus());
            }}
          >
            <Search size={20} aria-hidden="true" />
          </button>
          <SiteSearch
            {...search}
            onSearch={() => {
              setMobileSearchOpen(false);
              search.onSearch();
            }}
            onSuggestion={(suggestion) => {
              setMobileSearchOpen(false);
              search.onSuggestion?.(suggestion);
            }}
            compact
          />
          <button
            className="gk-nav-mobile-search-close"
            type="button"
            onClick={closeMobileSearch}
          >
            取消
          </button>
          <div className="gk-nav-account">
            <SiteAccountControls gameDomain={gameDomain} />
          </div>
        </div>
      </nav>
    </>
  );
}
